import React, { useEffect, useState } from "react";
import {
  ThreadListItemPrimitive,
  ThreadListPrimitive,
  useAssistantRuntime,
  useAuiState,
  useThread,
  useThreadListItem,
  useThreadListItemRuntime,
} from "@assistant-ui/react";
import {
  ChevronDown,
  EllipsisVertical,
  MessageSquare,
  Pencil,
  Plus,
  Trash2,
  X,
} from "lucide-react";
import { useStrings } from "./StringsContext";

// ─── Chat Header ─────────────────────────────────────────────────

export function ChatHeader() {
  const threadTitle = useAuiState(
    ({ threadListItem }) => threadListItem?.title ?? null,
  );
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const str = useStrings();
  const displayTitle = threadTitle || str.untitledChat;

  return (
    <div className="cuadra-relative">
      {/* Desktop: title + chevron */}
      <button
        type="button"
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        className="cuadra-hidden md:cuadra-flex cuadra-items-center cuadra-gap-1 cuadra-text-sm cuadra-text-foreground hover:cuadra-bg-muted/60 cuadra-rounded-lg cuadra-px-2 cuadra-py-1.5 cuadra-transition-colors cuadra-font-brand"
      >
        <span className="cuadra-font-medium cuadra-truncate cuadra-max-w-[200px]">
          {displayTitle}
        </span>
        <ChevronDown
          className={`cuadra-h-3.5 cuadra-w-3.5 cuadra-text-muted-foreground cuadra-flex-shrink-0 cuadra-transition-transform cuadra-duration-200 ${isDropdownOpen ? "cuadra-rotate-180" : ""}`}
        />
      </button>

      {/* Mobile: icon only */}
      <button
        type="button"
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        className="cuadra-flex md:cuadra-hidden cuadra-items-center cuadra-justify-center cuadra-text-foreground hover:cuadra-bg-muted/60 cuadra-rounded-lg cuadra-p-2 cuadra-transition-colors"
      >
        <MessageSquare className="cuadra-h-5 cuadra-w-5" />
      </button>

      {isDropdownOpen && (
        <ThreadListDropdown onClose={() => setIsDropdownOpen(false)} />
      )}
    </div>
  );
}

// ─── Thread List Dropdown ────────────────────────────────────────

function ThreadListDropdown({ onClose }: { onClose: () => void }) {
  const str = useStrings();
  return (
    <>
      {/* Backdrop — visible on desktop only */}
      <div
        className="cuadra-hidden md:cuadra-block cuadra-fixed cuadra-inset-0 cuadra-z-40"
        onClick={onClose}
      />

      {/* Mobile: fullscreen overlay */}
      <div
        className="cuadra-fixed cuadra-inset-0 cuadra-z-50 cuadra-flex cuadra-flex-col md:cuadra-hidden"
        style={{ backgroundColor: "var(--cuadra-popover)" }}
      >
        <div className="cuadra-flex cuadra-items-center cuadra-justify-end cuadra-px-4 cuadra-py-3 cuadra-min-h-[48px]">
          <button
            type="button"
            onClick={onClose}
            className="cuadra-flex cuadra-items-center cuadra-justify-center cuadra-p-1.5 cuadra-rounded-lg cuadra-text-muted-foreground hover:cuadra-bg-muted/60 cuadra-transition-colors"
          >
            <X className="cuadra-h-5 cuadra-w-5" />
          </button>
        </div>

        <ThreadListPrimitive.Root className="cuadra-flex cuadra-flex-col cuadra-flex-1 cuadra-min-h-0">
          <div className="cuadra-px-3 cuadra-py-2">
            <ThreadListPrimitive.New
              className="cuadra-w-full cuadra-flex cuadra-items-center cuadra-gap-2.5 cuadra-px-3 cuadra-py-2.5 cuadra-rounded-lg cuadra-text-sm cuadra-text-foreground hover:cuadra-bg-muted cuadra-transition-colors cuadra-cursor-pointer cuadra-font-brand"
              onClick={onClose}
            >
              <Plus className="cuadra-h-4 cuadra-w-4 cuadra-text-muted-foreground" />
              {str.newChat}
            </ThreadListPrimitive.New>
          </div>

          <DropdownThreadList onSelect={onClose} fullHeight />
        </ThreadListPrimitive.Root>
      </div>

      {/* Desktop: positioned dropdown */}
      <div
        className="cuadra-hidden md:cuadra-flex cuadra-absolute cuadra-left-0 cuadra-top-full cuadra-mt-1 cuadra-z-50 cuadra-w-[300px] cuadra-rounded-xl cuadra-overflow-hidden cuadra-flex-col"
        style={{
          backgroundColor: "var(--cuadra-popover)",
          boxShadow: "0 4px 24px rgba(0,0,0,0.12), 0 0 0 1px rgba(0,0,0,0.04)",
        }}
      >
        <ThreadListPrimitive.Root className="cuadra-flex cuadra-flex-col">
          <div className="cuadra-px-2 cuadra-py-1.5">
            <ThreadListPrimitive.New
              className="cuadra-w-full cuadra-flex cuadra-items-center cuadra-gap-2.5 cuadra-px-3 cuadra-py-2 cuadra-rounded-lg cuadra-text-sm cuadra-text-foreground hover:cuadra-bg-muted cuadra-transition-colors cuadra-cursor-pointer cuadra-font-brand"
              onClick={onClose}
            >
              <Plus className="cuadra-h-4 cuadra-w-4 cuadra-text-muted-foreground" />
              {str.newChat}
            </ThreadListPrimitive.New>
          </div>

          <DropdownThreadList onSelect={onClose} />
        </ThreadListPrimitive.Root>
      </div>
    </>
  );
}

// ─── Dropdown Thread List ────────────────────────────────────────

function DropdownThreadList({
  onSelect,
  fullHeight,
}: {
  onSelect?: () => void;
  fullHeight?: boolean;
}) {
  const threadIds = useAuiState(({ threads }) => threads?.threadIds ?? []);
  const newThreadId = useAuiState(({ threads }) => threads?.newThreadId);
  const hasChats = threadIds.some((id: string) => id !== newThreadId);

  if (!hasChats) return null;

  return (
    <>
      <div
        className="cuadra-mx-3 cuadra-my-0.5"
        style={{ borderTop: "1px solid var(--cuadra-border-subtle)" }}
      />
      <div
        className={`cuadra-overflow-y-auto cuadra-scrollbar-thin ${
          fullHeight
            ? "cuadra-flex-1 cuadra-min-h-0 cuadra-px-3 cuadra-pb-3 cuadra-flex cuadra-flex-col cuadra-gap-2"
            : "cuadra-px-2 cuadra-pb-1.5"
        }`}
        style={fullHeight ? undefined : { maxHeight: "340px" }}
      >
        <ThreadListPrimitive.Items
          components={{
            ThreadListItem: (props) => (
              <DropdownThreadItem
                {...props}
                onSelect={onSelect}
                fullHeight={fullHeight}
              />
            ),
          }}
        />
      </div>
    </>
  );
}

// ─── Dropdown Thread Item ────────────────────────────────────────

function DropdownThreadItem({
  onSelect,
  fullHeight,
}: {
  onSelect?: () => void;
  fullHeight?: boolean;
}) {
  const threadItem = useThreadListItem();
  const itemRuntime = useThreadListItemRuntime();
  const assistantRuntime = useAssistantRuntime();
  const currentThread = useThread((state) => state.threadId);
  const isActive = threadItem?.id === currentThread;
  const [isHovered, setIsHovered] = useState(false);
  const [isRenaming, setIsRenaming] = useState(false);
  const [nextTitle, setNextTitle] = useState(threadItem?.title || "");
  const [busy, setBusy] = useState(false);
  const str = useStrings();

  const handleRename = async () => {
    if (!threadItem?.remoteId || !nextTitle || nextTitle === threadItem.title) {
      setIsRenaming(false);
      return;
    }
    try {
      setBusy(true);
      await itemRuntime.rename(nextTitle);
    } catch {
      // Silently handle
    } finally {
      setBusy(false);
      setIsRenaming(false);
    }
  };

  const handleDelete = async (e: React.MouseEvent) => {
    e.stopPropagation();

    try {
      await itemRuntime.delete();

      if (isActive) {
        await assistantRuntime.switchToNewThread();
      }
    } catch (err) {
      // eslint-disable-next-line no-console -- Intentional: surface delete failures for debugging
      console.error("[CuadraChat] Failed to delete thread:", err);
    }
  };

  useEffect(() => {
    if (threadItem?.title && !isRenaming) setNextTitle(threadItem.title);
  }, [threadItem?.title, isRenaming]);

  if (!threadItem) return null;

  // Mobile fullscreen: card style with three-dot menu
  if (fullHeight) {
    return (
      <ThreadListItemPrimitive.Root
        className={`cuadra-relative cuadra-flex cuadra-items-center cuadra-rounded-xl cuadra-transition-colors cuadra-cursor-pointer ${
          isActive
            ? "cuadra-bg-primary/10 cuadra-ring-1 cuadra-ring-primary/20"
            : "cuadra-bg-muted/40"
        }`}
      >
        {isRenaming ? (
          <div
            className="cuadra-flex-1 cuadra-px-4 cuadra-py-3.5 cuadra-min-w-0"
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            onKeyUp={(e) => e.stopPropagation()}
            onKeyPress={(e) => e.stopPropagation()}
          >
            <input
              aria-label="Rename thread"
              className="cuadra-w-full cuadra-bg-transparent cuadra-border-0 cuadra-border-b cuadra-text-sm cuadra-text-foreground cuadra-py-0.5 cuadra-px-0 cuadra-outline-none cuadra-font-brand"
              style={{
                borderBottomColor: "var(--cuadra-primary)",
                borderBottomWidth: "1.5px",
              }}
              value={nextTitle}
              onChange={(e) => setNextTitle(e.target.value)}
              onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === "Enter") void handleRename();
                if (e.key === "Escape") {
                  setIsRenaming(false);
                  setNextTitle(threadItem.title || "");
                }
              }}
              onBlur={() => void handleRename()}
              disabled={busy}
              autoFocus
            />
          </div>
        ) : (
          <ThreadListItemPrimitive.Trigger
            className="cuadra-flex-1 cuadra-px-4 cuadra-py-3.5 cuadra-min-w-0 cuadra-cursor-pointer"
            onClick={() => onSelect?.()}
          >
            <span className="cuadra-text-sm cuadra-text-foreground cuadra-truncate cuadra-block cuadra-text-left cuadra-font-brand">
              {threadItem.title || str.chat}
            </span>
          </ThreadListItemPrimitive.Trigger>
        )}

        {!isRenaming && (
          <MobileThreadActions
            onRename={() => {
              setIsRenaming(true);
              setNextTitle(threadItem.title || str.chat);
            }}
            onDelete={handleDelete}
          />
        )}
      </ThreadListItemPrimitive.Root>
    );
  }

  // Desktop: compact list with hover-to-reveal actions
  return (
    <ThreadListItemPrimitive.Root
      className={`cuadra-relative cuadra-flex cuadra-items-center cuadra-rounded-lg cuadra-transition-colors cuadra-cursor-pointer cuadra-border-b cuadra-border-border/40 last:cuadra-border-b-0 ${
        isActive ? "cuadra-bg-muted" : "hover:cuadra-bg-muted/60"
      }`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {isRenaming ? (
        <div
          className="cuadra-flex-1 cuadra-px-3 cuadra-py-2 cuadra-min-w-0"
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          onKeyUp={(e) => e.stopPropagation()}
          onKeyPress={(e) => e.stopPropagation()}
        >
          <input
            aria-label="Rename thread"
            className="cuadra-w-full cuadra-bg-transparent cuadra-border-0 cuadra-border-b cuadra-text-sm cuadra-text-foreground cuadra-py-0.5 cuadra-px-0 cuadra-outline-none cuadra-font-brand"
            style={{
              borderBottomColor: "var(--cuadra-primary)",
              borderBottomWidth: "1.5px",
            }}
            value={nextTitle}
            onChange={(e) => setNextTitle(e.target.value)}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === "Enter") void handleRename();
              if (e.key === "Escape") {
                setIsRenaming(false);
                setNextTitle(threadItem.title || "");
              }
            }}
            onBlur={() => void handleRename()}
            disabled={busy}
            autoFocus
          />
        </div>
      ) : (
        <ThreadListItemPrimitive.Trigger
          className="cuadra-flex-1 cuadra-px-3 cuadra-py-2 cuadra-min-w-0 cuadra-cursor-pointer"
          onClick={() => onSelect?.()}
        >
          <span className="cuadra-text-sm cuadra-text-foreground cuadra-truncate cuadra-block cuadra-text-left cuadra-font-brand">
            {threadItem.title || str.chat}
          </span>
        </ThreadListItemPrimitive.Trigger>
      )}

      {(isHovered || isActive) && !isRenaming && (
        <div className="cuadra-flex cuadra-items-center cuadra-gap-0.5 cuadra-mr-1.5 cuadra-flex-shrink-0">
          <button
            type="button"
            className="cuadra-p-1 cuadra-rounded hover:cuadra-bg-background/60 cuadra-transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              setIsRenaming(true);
              setNextTitle(threadItem.title || str.chat);
            }}
            title={str.rename}
          >
            <Pencil className="cuadra-h-3 cuadra-w-3 cuadra-text-muted-foreground" />
          </button>
          <button
            type="button"
            className="cuadra-p-1 cuadra-rounded hover:cuadra-bg-destructive/10 cuadra-transition-colors"
            onClick={handleDelete}
            title={str.delete}
          >
            <Trash2 className="cuadra-h-3 cuadra-w-3 cuadra-text-destructive" />
          </button>
        </div>
      )}
    </ThreadListItemPrimitive.Root>
  );
}

// ─── Mobile Thread Actions ───────────────────────────────────────

function MobileThreadActions({
  onRename,
  onDelete,
}: {
  onRename: () => void;
  onDelete: (e: React.MouseEvent) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const str = useStrings();

  return (
    <div className="cuadra-relative cuadra-flex-shrink-0 cuadra-mr-2">
      <button
        type="button"
        className="cuadra-p-2 cuadra-rounded-lg cuadra-text-muted-foreground hover:cuadra-bg-background/60 cuadra-transition-colors"
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
      >
        <EllipsisVertical className="cuadra-h-4 cuadra-w-4" />
      </button>

      {isOpen && (
        <>
          <div
            className="cuadra-fixed cuadra-inset-0 cuadra-z-50"
            onClick={(e) => {
              e.stopPropagation();
              setIsOpen(false);
            }}
          />
          <div
            className="cuadra-absolute cuadra-right-0 cuadra-top-full cuadra-mt-1 cuadra-z-[60] cuadra-w-36 cuadra-rounded-xl cuadra-py-1 cuadra-overflow-hidden"
            style={{
              backgroundColor: "var(--cuadra-popover)",
              boxShadow:
                "0 4px 24px rgba(0,0,0,0.12), 0 0 0 1px rgba(0,0,0,0.04)",
            }}
          >
            <button
              type="button"
              className="cuadra-w-full cuadra-flex cuadra-items-center cuadra-gap-2.5 cuadra-px-3 cuadra-py-2.5 cuadra-text-sm cuadra-text-foreground hover:cuadra-bg-muted cuadra-transition-colors cuadra-font-brand"
              onClick={(e) => {
                e.stopPropagation();
                setIsOpen(false);
                onRename();
              }}
            >
              <Pencil className="cuadra-h-4 cuadra-w-4 cuadra-text-muted-foreground" />
              {str.rename}
            </button>
            <button
              type="button"
              className="cuadra-w-full cuadra-flex cuadra-items-center cuadra-gap-2.5 cuadra-px-3 cuadra-py-2.5 cuadra-text-sm cuadra-text-destructive hover:cuadra-bg-destructive/10 cuadra-transition-colors cuadra-font-brand"
              onClick={(e) => {
                e.stopPropagation();
                setIsOpen(false);
                onDelete(e);
              }}
            >
              <Trash2 className="cuadra-h-4 cuadra-w-4" />
              {str.delete}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
